import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import {
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Icon,
  Loading,
  PageHeader,
  Panel,
} from '../components/ui.js';
import { Badge as Pill } from '../components/2b-status.js';
import { SendTarget } from '../components/2b-sendtarget.js';
import { dateTime } from '../lib/format.js';
import { CHANNEL_LABEL, label as labelOf } from '../lib/labels.js';
import type { Rights } from '../lib/session.js';
import { useMe } from '../lib/session.js';
import { useBlockDnd } from '../components/2b-dnd.js';
import '../screens-2b.css';
import { STARTER_TEMPLATES, starterTemplateKey } from '@hub/shared';
import { ImagePickButton } from '../components/ImageGallery.js';
import { TemplateMaintenance } from './TemplateMaintenance.js';

/**
 * Editor šablon (SPEC-2 kap. 10). Bloky, ne volné HTML: uživatel skládá
 * obsah, vzhled drží motiv prodejce. Náhled renderuje server stejnou cestou
 * jako odeslání, takže co je vidět, to přijde.
 */

type BlockType =
  | 'heading'
  | 'text'
  | 'button'
  | 'image'
  | 'hero'
  | 'nav'
  | 'cover'
  | 'pricebox'
  | 'features'
  | 'offers'
  | 'racelist'
  | 'callout'
  | 'banner'
  | 'deadline'
  | 'signoff'
  | 'promo'
  | 'deal'
  | 'ctarow'
  | 'crosspromo'
  | 'points'
  | 'kicker'
  | 'stats'
  | 'split'
  | 'countdown'
  | 'quote'
  | 'cards'
  | 'race'
  | 'trips'
  | 'orderitems'
  | 'divider'
  | 'spacer'
  | 'footer';

interface Block {
  type: BlockType;
  [key: string]: unknown;
}

interface Doc {
  subject: string;
  preheader?: string;
  blocks: Block[];
}

interface CodeInfo {
  code: string;
  name: string;
  channel: 'marketing' | 'service';
  scope: string;
  sender: string;
  /** Základ klíče šablony z katalogu — engine hledá podle něj. */
  templateKey: string;
}

interface TemplateRow {
  templateKey: string;
  code: string;
  seller: string;
  language: string;
  name: string;
  publishedVersion: number | null;
  draftVersion: number | null;
}

interface SyncInfo {
  ok: boolean;
  emailId?: string;
  skipped?: boolean;
  error?: string;
}

interface Detail {
  template: TemplateRow;
  versions: Array<{
    version: number;
    status: string;
    subject: string;
    publishedAt: string | null;
  }>;
  doc: Doc | null;
  editing: 'draft' | 'published';
  variables: string[];
  channel: 'marketing' | 'service';
}

const LANGUAGES = ['cs', 'en', 'it', 'de'];

const NEW_BLOCK: Record<BlockType, Block> = {
  heading: { type: 'heading', level: 2, text: 'Nadpis' },
  text: { type: 'text', html: 'Text zprávy.' },
  button: { type: 'button', text: 'Otevřít', url: 'https://', variant: 'primary' },
  image: { type: 'image', url: 'https://', alt: '' },
  nav: {
    type: 'nav',
    brand: 'Tickets',
    brandAccent: 'GP',
    links: [{ text: 'Vstupenky', url: 'https://' }],
  },
  cover: {
    type: 'cover',
    image: 'https://',
    alt: '',
    title: 'Hlavní nadpis',
    subtitle: '',
    height: 420,
    size: 'large',
  },
  banner: {
    type: 'banner',
    eyebrow: '',
    title: 'Hlavní nadpis',
    subtitle: '',
    tone: 'dark',
  },
  deadline: { type: 'deadline', title: 'Sleva *končí* v neděli.', text: '' },
  signoff: { type: 'signoff', text: 'Uvidíme se na okruhu!', team: 'Tým TicketsGP' },
  promo: { type: 'promo', label: 'KÓD', code: 'FIESTA', note: 'Jen do neděle 23:59' },
  deal: {
    type: 'deal',
    badge: 'Nejlepší cena',
    title: 'Název nabídky',
    oldPrice: '',
    price: '0 €',
    save: '',
    strip: '',
    alt: '',
  },
  ctarow: { type: 'ctarow', text: 'Další vstupenky – zobrazit vše', url: 'https://' },
  crosspromo: { type: 'crosspromo', title: 'Nadpis', text: '', alt: '' },
  points: {
    type: 'points',
    items: [{ icon: '🏎️', title: 'Titulek', text: 'Krátký popis.' }],
  },
  kicker: { type: 'kicker', text: 'Nadpis sekce' },
  stats: {
    type: 'stats',
    items: [
      { value: '3.–5. 9.', label: 'termín' },
      { value: 'od 79 €', label: 'vstupenky' },
    ],
  },
  split: { type: 'split', title: 'Nadpis', html: 'Text.', imageSide: 'right', alt: '' },
  countdown: { type: 'countdown', label: 'Do závodu zbývá', value: 'méně než týden' },
  quote: { type: 'quote', text: 'Tip nebo citace.' },
  pricebox: {
    type: 'pricebox',
    label: 'Vstupenky',
    price: 'od 199 €*',
    button: { text: 'Vybrat vstupenky →', url: 'https://' },
  },
  features: {
    type: 'features',
    items: [
      { icon: '⭐', label: 'První výhoda' },
      { icon: '⭐', label: 'Druhá výhoda' },
      { icon: '⭐', label: 'Třetí výhoda' },
    ],
  },
  offers: {
    type: 'offers',
    items: [
      {
        image: 'https://',
        alt: '',
        title: 'Název',
        desc: 'Krátký popis',
        price: 'od 199 €*',
        button: 'Zobrazit možnosti',
        url: 'https://',
      },
    ],
  },
  racelist: {
    type: 'racelist',
    items: [
      {
        image: 'https://',
        alt: '',
        series: 'F1',
        name: 'Název závodu',
        meta: 'Termín • místo',
        price: 'od 199 €*',
        button: 'Vybrat vstupenky →',
        url: 'https://',
      },
    ],
  },
  callout: {
    type: 'callout',
    icon: '🔔',
    title: 'Nadpis upozornění',
    text: 'Krátký text.',
    button: { text: 'Chci upozornění', url: 'https://' },
  },
  hero: {
    type: 'hero',
    image: 'https://',
    alt: '',
    eyebrow: '',
    title: 'Hlavní nadpis',
    html: 'Úvodní text.',
    button: { text: 'Zjistit víc  →', url: 'https://' },
  },
  cards: {
    type: 'cards',
    columns: 2,
    items: [
      { title: 'Zavod 1', alt: '', image: '', url: '', button: 'Koupit' },
      { title: 'Zavod 2', alt: '', image: '', url: '', button: 'Koupit' },
    ],
  },
  race: { type: 'race', show: ['date', 'place', 'tickets'] },
  trips: { type: 'trips' },
  orderitems: { type: 'orderitems' },
  divider: { type: 'divider' },
  spacer: { type: 'spacer', size: 'm' },
  footer: { type: 'footer' },
};

const BLOCK_LABELS: Record<BlockType, string> = {
  heading: 'Nadpis',
  text: 'Text',
  button: 'Tlačítko',
  image: 'Obrázek',
  nav: 'Navigační lišta',
  cover: 'Úvodní plocha (text přes fotku)',
  pricebox: 'Cena a tlačítko',
  banner: 'Pruh s nadpisem (volitelně fotka)',
  stats: 'Dlaždice s čísly',
  points: 'Řádky s ikonou a titulkem',
  promo: 'Box s promo kódem',
  deadline: 'Řádek „sleva končí“',
  signoff: 'Podpis týmu',
  deal: 'Karta nabídky se slevou',
  ctarow: 'Řádek „zobrazit vše“',
  crosspromo: 'Červená promo karta (NewsGP)',
  kicker: 'Červený nadpis sekce',
  split: 'Text a obrázek vedle sebe',
  countdown: 'Odpočet (statický text)',
  quote: 'Citace / tip',
  features: 'Výhody s ikonami',
  offers: 'Nabídka řádků (vstupenky)',
  racelist: 'Přehled závodů',
  callout: 'Upozornění / pomoc',
  hero: 'Úvodní plocha (fotka + nadpis)',
  cards: 'Mřížka karet (závody)',
  race: 'Box závodu',
  trips: 'Seznam cest',
  orderitems: 'Položky objednávky',
  divider: 'Oddělovač',
  spacer: 'Mezera',
  footer: 'Patička s odhlášením',
};

const emptyDoc = (): Doc => ({
  subject: '',
  blocks: [{ ...NEW_BLOCK.heading }, { ...NEW_BLOCK.text }, { ...NEW_BLOCK.footer }],
});

/* Vzory jsou ve sdilenem balicku — editor i server berou tentyz obsah. */
const PRESETS = STARTER_TEMPLATES;

const BLOCK_ICON: Record<BlockType, string> = {
  nav: '☰',
  cover: '◩',
  pricebox: '€',
  banner: '▬',
  stats: '▦',
  points: '☰',
  promo: '%',
  deadline: '⏳',
  signoff: '✍',
  deal: '€',
  ctarow: '→',
  crosspromo: '★',
  kicker: 'K',
  split: '◧',
  countdown: '⏱',
  quote: '❝',
  features: '✦',
  offers: '▤',
  racelist: '▥',
  callout: '!',
  hero: '▣',
  heading: 'H',
  text: '¶',
  button: '⬭',
  image: '▨',
  cards: '▦',
  race: '⚑',
  trips: '☰',
  orderitems: '▤',
  divider: '—',
  spacer: '↕',
  footer: '▁',
};

const BLOCK_HINT: Record<BlockType, string> = {
  nav: 'Tmavá lišta nahoře: značka vlevo, odkazy vpravo. Nahrazuje logo a červený pruh.',
  cover: 'Fotka na celou šířku s textem přes ni; text leží na tmavém přechodu, aby byl čitelný.',
  pricebox: 'Cena a hlavní tlačítko v jednom šedém boxu.',
  banner: 'Tmavý pruh s velkým nadpisem a tlačítkem; fotka nad ním je volitelná.',
  deadline: 'Text vlevo (část v *hvězdičkách* červeně), tlačítko s datem vpravo.',
  signoff: 'Pozdrav a podpis týmu červeně na závěr zprávy.',
  promo: 'Kód ve čárkovaném rámečku na tmavé ploše a pod ním podmínky.',
  deal: 'Štítek, název, obrázek (mapka), přeškrtnutá cena, odznak úspory, tmavý pruh a tlačítko.',
  ctarow: 'Verzálkový odkaz se šipkou mezi dvěma čarami.',
  crosspromo: 'Červená karta s nadpisem, textem, bílým tlačítkem a obrázkem (mockupy).',
  points: 'Ikona, verzálkový titulek a krátký popis pod sebou (např. důvody, proč jet).',
  kicker: 'Červený verzálkový nadpis sekce na střed.',
  stats: 'Dvě až čtyři dlaždice s hodnotou a popiskem (termín, cena, počet dní).',
  split: 'Text vedle obrázku; na telefonu pod sebou. Obrázek je volitelný.',
  countdown: 'Velká hodnota na tmavé ploše. Text píšete sami, nic se nepočítá.',
  quote: 'Citace nebo tip s pruhem v barvě značky.',
  features: 'Tři až čtyři výhody v řádku s ikonou (znak nebo emoji) a popiskem.',
  offers: 'Řádky s fotkou, názvem, popisem, cenou a tlačítkem — např. kategorie vstupenek.',
  racelist: 'Přehled závodů: fotka vlevo, vpravo série, název, termín, cena a tlačítko.',
  callout: 'Šedý box s ikonou a textem; vpravo odkaz, nebo pod textem tlačítko.',
  hero: 'Fotka přes celou šířku a pod ní tmavý panel s nadpisem, textem a tlačítkem.',
  heading: 'Nadpis, volitelně s malým nadpisem nad ním.',
  text: 'Odstavec textu. Povolené je tučné, kurzíva a odkaz.',
  button: 'Tlačítko s odkazem — hlavní výzva k akci.',
  image: 'Obrázek nebo animace přes šířku obsahu.',
  cards: 'Mřížka karet — typicky nabídka závodů s termínem a tlačítkem.',
  race: 'Box s údaji o závodu zákazníka (jen u servisních zpráv).',
  trips: 'Seznam cest u sloučené zprávy.',
  orderitems: 'Tabulka položek objednávky; hotovou ji dodá hub (jen u transakčních zpráv T).',
  divider: 'Tenká oddělovací čára.',
  spacer: 'Prázdné místo mezi bloky.',
  footer: 'Patička s odhlášením a kontakty. U marketingu povinná.',
};

/** Krátký popis bloku do seznamu — ať je poznat, který je který. */
function summary(block: Block): string {
  const plain = (v: unknown): string =>
    String(v ?? '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  switch (block.type) {
    case 'hero':
      return plain(block.title);
    case 'heading':
      return plain(block.text);
    case 'text':
      return plain(block.html).slice(0, 70);
    case 'button':
      return plain(block.text);
    case 'image':
      return plain(block.alt) || plain(block.url).slice(-32);
    case 'cards':
      return `${(block.items as unknown[] | undefined)?.length ?? 0} karet`;
    case 'cover':
      return plain(block.title);
    case 'pricebox':
      return plain(block.price);
    case 'banner':
    case 'split':
      return plain(block.title);
    case 'kicker':
      return plain(block.text);
    case 'promo':
      return plain(block.code);
    case 'deadline':
      return plain(block.title);
    case 'signoff':
      return plain(block.text);
    case 'deal':
    case 'crosspromo':
      return plain(block.title);
    case 'ctarow':
      return plain(block.text);
    case 'points':
      return `${(block.items as unknown[] | undefined)?.length ?? 0} řádků`;
    case 'countdown':
      return plain(block.value);
    case 'quote':
      return plain(block.text).slice(0, 60);
    case 'callout':
      return plain(block.title);
    case 'nav':
      return plain(block.brand);
    case 'stats':
    case 'features':
    case 'offers':
    case 'racelist':
      return `${(block.items as unknown[] | undefined)?.length ?? 0} položek`;
    case 'footer':
      return plain(block.note).slice(0, 50);
    default:
      return '';
  }
}

interface SendTestOverview {
  mode: 'dry_run' | 'test_allowlist';
  recipients: Array<{ address: string; masked: string }>;
  items: Array<{ templateKey: string; testable: boolean; reason: string | null }>;
}

const SANDBOX_ONLY =
  'Zkušební odesílání funguje jen v prostředí, kde se zprávy smějí posílat nanečisto nebo na zkušební adresy.';

const VERSION_STATUS: Record<string, string> = {
  published: 'publikovaná',
  draft: 'koncept',
  archived: 'dřívější verze',
};

/**
 * „Poslat test“ z editoru: stejná cesta jako Komunikace → Test odesílání (D-120).
 * Pošle se PUBLIKOVANÁ verze šablony, vykreslená hubem; příjemce je ze seznamu zkušebních adres.
 */
function TestSendPanel({
  templateKey,
  onClose,
}: {
  templateKey: string;
  onClose: () => void;
}): JSX.Element {
  const me = useMe();
  const live = me.data?.sendMode === 'live' || me.data?.sendModeCeiling === 'live';
  const [recipient, setRecipient] = useState('');
  const [result, setResult] = useState<{ tone: 'ok' | 'bad' | 'neu'; text: string } | null>(null);
  const overview = useQuery<SendTestOverview>({
    queryKey: ['send-tests'],
    queryFn: () => api.get<SendTestOverview>('/api/send-tests'),
    enabled: !live && me.isSuccess,
    retry: false,
  });
  const blocked =
    live ||
    (overview.error instanceof ApiError && overview.error.code === 'only_in_test_environment');
  const item = overview.data?.items.find((i) => i.templateKey === templateKey);
  const recipients = overview.data?.recipients ?? [];
  const chosen = recipient || (recipients.length === 1 ? (recipients[0]?.address ?? '') : '');
  const send = useMutation({
    mutationFn: () =>
      api.post<{
        run: {
          status: string;
          provider: string | null;
          recipientMasked: string;
          subject: string | null;
          error: string | null;
        };
      }>('/api/send-tests/send', { templateKey, ...(chosen ? { recipient: chosen } : {}) }),
    onSuccess: ({ run }) =>
      setResult(
        run.status === 'sent' && run.provider === 'mock'
          ? {
              tone: 'bad',
              text: 'Zkušební odesílač: zpráva se jen simulovala, nic neodešlo ven. Napojte SmartEmailing v Nastavení → Napojení.',
            }
          : run.status === 'sent'
            ? {
                tone: 'ok',
                text: `Zkušební zpráva odešla přes SmartEmailing na ${run.recipientMasked}${run.subject ? ` (předmět „${run.subject}“)` : ''}. Dorazí do minuty; reakce uvidíte v Komunikace → Test odesílání.`,
              }
            : run.status === 'dry_run'
              ? {
                  tone: 'neu',
                  text: 'Režim „Nanečisto“: nic se neodeslalo. Zpráva se jen vykreslila a zapsala do Komunikace → Test odesílání. Skutečně odejde až v prostředí, kde se smí posílat na zkušební adresy.',
                }
              : { tone: 'bad', text: `Odeslání selhalo: ${run.error ?? run.status}` },
      ),
    onError: (err: Error) =>
      setResult({
        tone: 'bad',
        text:
          err instanceof ApiError && err.code === 'only_in_test_environment'
            ? SANDBOX_ONLY
            : err.message,
      }),
  });
  return (
    <div
      className="confirm"
      role="region"
      aria-label="Poslat test"
      data-testid="template-test-panel"
    >
      <b>Poslat zkušební zprávu</b>
      {me.data && !blocked ? <SendTarget me={me.data} compact /> : null}
      {blocked ? (
        <span role="alert">{SANDBOX_ONLY}</span>
      ) : overview.isLoading || me.isLoading ? (
        <span>Načítám…</span>
      ) : overview.error ? (
        <span role="alert">{overview.error.message}</span>
      ) : (
        <>
          <span>
            Pošle se <b>publikovaná</b> verze šablony, vykreslená hubem (neuložený koncept se do
            testu nepromítne).{' '}
            {overview.data?.mode === 'dry_run'
              ? 'Režim je „Nanečisto“: nic se neodešle, jen se vykreslí a zapíše.'
              : 'Příjemce musí být na seznamu zkušebních adres.'}
          </span>
          {!item ? (
            <span role="alert">
              Tahle šablona nemá publikovanou verzi (nebo není v katalogu) — nejdřív ji publikujte.
            </span>
          ) : !item.testable ? (
            <span role="alert">{item.reason ?? 'Tuhle šablonu nelze zkoušet.'}</span>
          ) : null}
          <div className="row">
            {recipients.length > 1 ? (
              <select
                aria-label="Příjemce testu"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              >
                <option value="">— vyberte příjemce —</option>
                {recipients.map((r) => (
                  <option key={r.address} value={r.address}>
                    {r.masked}
                  </option>
                ))}
              </select>
            ) : recipients.length === 1 ? (
              <span>Příjemce: {recipients[0]?.masked}</span>
            ) : (
              <span>Není nastavená žádná zkušební adresa, takže není komu poslat.</span>
            )}
            <button
              className="btn pri"
              disabled={send.isPending || !item?.testable || (recipients.length > 1 && !chosen)}
              onClick={() => {
                setResult(null);
                send.mutate();
              }}
            >
              {send.isPending ? 'Odesílám…' : 'Poslat test'}
            </button>
            <button className="btn" onClick={onClose}>
              Zavřít
            </button>
          </div>
        </>
      )}
      {result ? (
        <span role="status" data-testid="template-test-result">
          {result.text}
        </span>
      ) : null}
    </div>
  );
}

/** Viditelné místo vložení při tažení bloku. */
function DropMark({ label }: { label: string }): JSX.Element {
  return (
    <li className="dropmark" aria-hidden="true">
      <span>Sem se vloží: {label}</span>
    </li>
  );
}

export function TemplateEditor({
  rights,
  initialKey,
}: {
  rights: Rights;
  /** Šablona, kterou otevřít hned (odkaz „Upravit šablonu“ z Odesílání); `*` na konci = první šablona rodiny. */
  initialKey?: string;
}): JSX.Element {
  const client = useQueryClient();
  const canEdit = rights.can('map_templates');

  const list = useQuery({
    queryKey: ['template-list'],
    queryFn: () => api.get<{ items: TemplateRow[]; codes: CodeInfo[] }>('/api/templates'),
  });

  const [code, setCode] = useState('');
  const [seller, setSeller] = useState('TGP');
  const [language, setLanguage] = useState('cs');
  const codes = list.data?.codes ?? [];
  const selected = codes.find((c) => c.code === code);
  /* Klic sablony je `<template_key>_<prodejce>_<jazyk>` (SPEC kap. 6.4). */
  /* Rozesílky (kód BC) mají v klíči označení rozesílky: `BC_<označení>_<prodejce>_<jazyk>`. */
  const [variant, setVariant] = useState('');
  const key = selected
    ? `${selected.templateKey}${code === 'BC' && variant ? `_${variant}` : ''}_${seller}_${language}`
    : '';

  const detail = useQuery({
    queryKey: ['template', key],
    enabled: key !== '',
    /* Šablona ještě nemusí existovat — 404 znamená „nová“, ne chybu. */
    queryFn: async (): Promise<Detail | null> => {
      try {
        return await api.get<Detail>(`/api/templates/${encodeURIComponent(key)}`);
      } catch {
        return null;
      }
    },
  });

  const openRow = (item: TemplateRow): void => {
    setCode(item.code);
    setSeller(item.seller);
    setLanguage(item.language);
    setVariant(
      item.code === 'BC'
        ? item.templateKey
            .slice(2, item.templateKey.length - `_${item.seller}_${item.language}`.length)
            .replace(/^_/, '')
        : '',
    );
  };
  /* Odkaz z Odesílání: otevře se právě ta šablona (nebo první jazyk rodiny rozesílky). */
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!initialKey || !list.data || openedFor.current === initialKey) return;
    const wild = initialKey.endsWith('*');
    const row = list.data.items.find((i) =>
      wild ? i.templateKey.startsWith(initialKey.slice(0, -1)) : i.templateKey === initialKey,
    );
    openedFor.current = initialKey;
    if (row) openRow(row);
  }, [initialKey, list.data]);

  const [doc, setDoc] = useState<Doc>(emptyDoc);
  const [customerId, setCustomerId] = useState('');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  /* Světlý je výchozí: náhled jinak dědí režim prohlížeče a v tmavém systému
     by se vždycky ukázal tmavý e-mail, i když ho většina lidí uvidí světlý. */
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLOListElement>(null);
  /* Oznámení pro čtečky a návrat zaměření na tlačítko po přesunu bloku. */
  const [announce, setAnnounce] = useState('');
  const [askPublish, setAskPublish] = useState(false);
  const [askRevert, setAskRevert] = useState<number | null>(null);
  const refocus = useRef<{ index: number; dir: 'up' | 'down' } | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [issues, setIssues] = useState<Array<{ code: string; detail: string }>>([]);
  const [note, setNote] = useState<string | null>(null);
  /* Výsledek nahrání marketingové šablony do SmartEmailingu (odpověď publikace). */
  const [sync, setSync] = useState<SyncInfo | null>(null);
  /* Hledání, filtr a řazení seznamu šablon; sady (např. Monza) drží pohromadě řazení podle klíče. */
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [sortBy, setSortBy] = useState<'key' | 'name'>('key');
  /* Neuložené změny: dokument se porovná s tím, co přišlo ze serveru nebo co se naposledy uložilo. */
  const [baseline, setBaseline] = useState(() => JSON.stringify(emptyDoc()));
  const dirty = code !== '' && JSON.stringify(doc) !== baseline;

  useEffect(() => {
    if (!key) return;
    const loaded = detail.data?.doc ?? emptyDoc();
    setDoc(loaded);
    setBaseline(JSON.stringify(loaded));
    setIssues([]);
    setNote(null);
  }, [key, detail.data]);

  const variables = detail.data?.variables ?? [];

  /* `mutate` ma stabilni identitu; cely objekt mutace ne — v zavislostech
     efektu by se nahled prekresloval donekonecna. */
  const preview = useMutation({
    mutationFn: () =>
      api.post<{ html: string; subject: string; missingVars: string[] }>('/api/templates/preview', {
        code,
        seller,
        doc,
        scheme,
        language,
        ...(customerId ? { customerId } : {}),
      }),
  });

  /* Náhled se překresluje po úpravě, ne při psaní každého znaku. */
  const previewMutate = preview.mutate;
  useEffect(() => {
    if (!code) return;
    const id = setTimeout(() => previewMutate(), 400);
    return () => clearTimeout(id);
  }, [doc, code, seller, customerId, scheme, language, previewMutate]);

  /**
   * Klikání v náhledu. Rámec je stejného původu, takže na jeho dokument jde
   * z administrace připojit posluchač — skript v e-mailu k tomu není potřeba
   * (a nesměl by tam být).
   */
  const wireFrame = useCallback((): void => {
    const d = frameRef.current?.contentDocument;
    if (!d?.head || !d.body) return;
    let style = d.getElementById('hub-edit-style');
    if (!style) {
      style = d.createElement('style');
      style.id = 'hub-edit-style';
      d.head.appendChild(style);
    }
    style.textContent =
      '.hub-b{cursor:pointer}.hub-b:hover{outline:2px dashed #e6333f;outline-offset:-2px}' +
      (openIndex === null
        ? ''
        : `.hub-b-${openIndex}{outline:2px solid #e6333f;outline-offset:-2px}`);
    if (d.body.dataset.wired) return;
    d.body.dataset.wired = '1';
    d.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        const hit = (event.target as Element | null)?.closest('[class*="hub-b-"]');
        const match = hit ? /hub-b-(\d+)/.exec(hit.className) : null;
        if (match) setOpenIndex(Number(match[1]));
      },
      true,
    );
  }, [openIndex]);

  useEffect(() => {
    wireFrame();
  }, [wireFrame, preview.data?.html]);

  /* Vybraný blok se v náhledu doscrolluje, ať je vidět, co se upravuje. */
  useEffect(() => {
    if (openIndex === null) return;
    frameRef.current?.contentDocument
      ?.querySelector(`.hub-b-${openIndex}`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [openIndex]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/templates/${encodeURIComponent(key)}`, {
        code,
        seller,
        language,
        name: selected?.name ?? code,
        doc,
      }),
    onSuccess: () => {
      setBaseline(JSON.stringify(doc));
      setNote('Koncept uložen.');
      setIssues([]);
      void client.invalidateQueries({ queryKey: ['template', key] });
      void client.invalidateQueries({ queryKey: ['template-list'] });
    },
    onError: (err: Error) => setNote(err.message),
  });

  const publish = useMutation({
    mutationFn: () =>
      api.post<{ sync?: SyncInfo }>(`/api/templates/${encodeURIComponent(key)}/publish`, {}),
    onSuccess: (res) => {
      setSync(res.sync ?? null);
      setNote('Publikováno. Engine od teď posílá tuhle verzi.');
      setIssues([]);
      void client.invalidateQueries({ queryKey: ['template', key] });
      void client.invalidateQueries({ queryKey: ['template-list'] });
    },
    onError: (err: Error) => {
      /* Kontrola před publikací vrací seznam důvodů (SPEC-2 kap. 10.5). */
      try {
        const parsed = JSON.parse(err.message) as {
          issues?: Array<{ code: string; detail: string }>;
        };
        setIssues(parsed.issues ?? []);
        setNote(null);
      } catch {
        setNote(err.message);
      }
    },
  });

  const retrySync = useMutation({
    mutationFn: () => api.post<SyncInfo>(`/api/templates/${encodeURIComponent(key)}/sync`, {}),
    onSuccess: (res) => setSync(res),
    onError: (err: Error) => setSync({ ok: false, error: err.message }),
  });

  const [testOpen, setTestOpen] = useState(false);

  const installStarters = useMutation({
    mutationFn: () =>
      api.post<{ installed: Array<{ key: string }>; skipped: string[] }>(
        '/api/templates/install-starters',
        { overwrite: false },
      ),
    onSuccess: (data) => {
      setNote(
        data.installed.length > 0
          ? `Nahráno: ${data.installed.map((i) => i.key).join(', ')}. Vyberte je nahoře a publikujte.`
          : 'Hotové šablony už v hubu jsou.',
      );
      void client.invalidateQueries({ queryKey: ['template-list'] });
    },
    onError: (err: Error) => setNote(err.message),
  });

  const revert = useMutation({
    mutationFn: (version: number) =>
      api.post(`/api/templates/${encodeURIComponent(key)}/revert`, { version }),
    onSuccess: () => {
      setNote('Vráceno na dřívější verzi.');
      void client.invalidateQueries({ queryKey: ['template', key] });
    },
  });

  const update = (index: number, patch: Partial<Block>): void =>
    setDoc({
      ...doc,
      blocks: doc.blocks.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    });

  const addBlock = (type: BlockType, where?: number): void => {
    /* Vloží se na místo puštění, jinak pod vybraný blok, jinak na konec. */
    const at = where ?? (openIndex === null ? doc.blocks.length : openIndex + 1);
    const blocks = [...doc.blocks];
    blocks.splice(at, 0, JSON.parse(JSON.stringify(NEW_BLOCK[type])) as Block);
    setDoc({ ...doc, blocks });
    setOpenIndex(at);
    setAnnounce(`Blok ${BLOCK_LABELS[type]} je přidaný na pozici ${at + 1} z ${blocks.length}.`);
  };

  /** Obrázek z galerie se vloží jako nový blok Obrázek (pod vybraný blok, jinak na konec). */
  const addImageFromGallery = (url: string): void => {
    const at = openIndex === null ? doc.blocks.length : openIndex + 1;
    const blocks = [...doc.blocks];
    blocks.splice(at, 0, { ...(JSON.parse(JSON.stringify(NEW_BLOCK.image)) as Block), url });
    setDoc({ ...doc, blocks });
    setOpenIndex(at);
    setAnnounce(`Obrázek z galerie je přidaný na pozici ${at + 1} z ${blocks.length}.`);
  };

  const removeBlock = (index: number): void => {
    setDoc({ ...doc, blocks: doc.blocks.filter((_, i) => i !== index) });
    setOpenIndex(null);
  };

  const duplicateBlock = (index: number): void => {
    const blocks = [...doc.blocks];
    blocks.splice(index + 1, 0, JSON.parse(JSON.stringify(doc.blocks[index])) as Block);
    setDoc({ ...doc, blocks });
    setOpenIndex(index + 1);
  };

  const moveTo = (from: number, to: number): void => {
    if (from === to || to < 0 || to >= doc.blocks.length) return;
    const blocks = [...doc.blocks];
    const [moved] = blocks.splice(from, 1);
    blocks.splice(to, 0, moved as Block);
    setDoc({ ...doc, blocks });
    setOpenIndex(to);
    setAnnounce(
      `Blok ${BLOCK_LABELS[(moved as Block).type]} je teď na pozici ${to + 1} z ${blocks.length}.`,
    );
  };

  /* Tlačítka nahoru/dolů: totéž co tažení, jen z klávesnice. */
  const move = (index: number, by: number): void => {
    const target = index + by;
    if (target < 0 || target >= doc.blocks.length) return;
    refocus.current = { index: target, dir: by < 0 ? 'up' : 'down' };
    const was = openIndex;
    moveTo(index, target);
    setOpenIndex(was === index ? target : was === target ? index : was);
  };

  const dnd = useBlockDnd({
    listRef,
    enabled: canEdit,
    onDrop: (src, at) => {
      if (src.kind === 'new') addBlock(src.type as BlockType, at);
      else moveTo(src.index, at > src.index ? at - 1 : at);
    },
  });

  /* Po přesunu tlačítkem zůstane zaměření na stejném bloku (jinak by zůstalo na pozici). */
  useEffect(() => {
    const target = refocus.current;
    if (!target) return;
    refocus.current = null;
    const items = listRef.current?.querySelectorAll(':scope > li.blk');
    const atEdge =
      target.dir === 'up' ? target.index === 0 : target.index === doc.blocks.length - 1;
    const sel = atEdge ? `.mv-${target.dir === 'up' ? 'down' : 'up'}` : `.mv-${target.dir}`;
    items?.[target.index]?.querySelector<HTMLButtonElement>(sel)?.focus();
  }, [doc]);

  const needle = query.trim().toLocaleLowerCase('cs');
  const visibleRows = (list.data?.items ?? [])
    .filter((i) => {
      if (statusFilter === 'published' && !i.publishedVersion) return false;
      if (statusFilter === 'draft' && i.publishedVersion) return false;
      return (
        needle === '' ||
        `${i.name} ${i.code} ${i.templateKey}`.toLocaleLowerCase('cs').includes(needle)
      );
    })
    .sort((a, b) =>
      sortBy === 'name'
        ? a.name.localeCompare(b.name, 'cs')
        : a.templateKey.localeCompare(b.templateKey, 'cs'),
    );

  if (list.isLoading) return <Loading what="šablony" />;
  if (list.isError)
    return (
      <ErrorState
        title="Šablony se nepodařilo načíst"
        error={list.error}
        onRetry={() => void list.refetch()}
      />
    );

  return (
    <>
      <PageHeader
        level={2}
        title="Šablony e-mailů"
        description="Obsah e-mailů skládáte z bloků, vzhled drží motiv prodejce. Změna je nejdřív koncept a zákazníci ji dostanou až po publikaci."
      />
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}

      <Panel
        title="Šablony v hubu"
        action={
          canEdit ? (
            <button className="btn" onClick={() => installStarters.mutate()}>
              Nahrát hotové šablony
            </button>
          ) : null
        }
      >
        <div className="help">
          <p>
            Tohle jsou šablony, které v hubu existují. Kliknutím ji otevřete níž k úpravě.{' '}
            <b>Koncept</b> engine neposílá — publikovaná verze ano.
          </p>
        </div>
        {(list.data?.items.length ?? 0) === 0 ? (
          <p className="note">
            Zatím tu žádná šablona není. Začněte tlačítkem <b>Nahrát hotové šablony</b>, nebo si níž
            vyberte kód zprávy a složte vlastní.
          </p>
        ) : (
          <>
            <div className="row">
              <input
                type="search"
                aria-label="Hledat šablonu"
                placeholder="Hledat podle názvu, kódu nebo klíče (např. monza)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <select
                aria-label="Stav šablon"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'published' | 'draft')}
              >
                <option value="all">všechny</option>
                <option value="published">publikované</option>
                <option value="draft">jen koncept</option>
              </select>
              <select
                aria-label="Řazení"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'key' | 'name')}
              >
                <option value="key">řadit podle klíče (sady pohromadě)</option>
                <option value="name">řadit podle názvu</option>
              </select>
              <span className="note">
                {visibleRows.length} z {list.data?.items.length ?? 0}
              </span>
            </div>
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Zpráva</th>
                    <th>Název</th>
                    <th>Prodejce</th>
                    <th>Jazyk</th>
                    <th>Stav</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((item) => (
                    <tr key={item.templateKey} className={key === item.templateKey ? 'sel' : ''}>
                      <td>
                        <b>{item.code}</b>
                      </td>
                      <td>{item.name}</td>
                      <td>{item.seller}</td>
                      <td>{item.language}</td>
                      <td>
                        {item.publishedVersion ? (
                          <Pill tone="ok">publikovaná v{item.publishedVersion}</Pill>
                        ) : (
                          <Pill tone="warn">jen koncept</Pill>
                        )}
                        {item.draftVersion && item.publishedVersion ? (
                          <Pill tone="warn">rozepsaný koncept</Pill>
                        ) : null}
                      </td>
                      <td>
                        <button className="btn" onClick={() => openRow(item)}>
                          Otevřít
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      <TemplateMaintenance canWrite={canEdit} />
      <Panel title="Která zpráva">
        <div className="help">
          <p>
            Šablona patří ke <b>kódu zprávy</b>, prodejci a jazyku. Engine hledá přesně tuhle
            trojici; když šablona pro jazyk chybí, použije anglickou, a když chybí i ta, zprávu{' '}
            <b>nepošle</b> a drží ji s důvodem „chybí šablona“.
          </p>
          <p>
            Vzhled drží motiv prodejce — vy skládáte obsah z bloků. Publikovaná verze je ta, kterou
            engine posílá; úprava vzniká jako koncept.
          </p>
        </div>
        <div className="row">
          <select value={code} aria-label="Kód zprávy" onChange={(e) => setCode(e.target.value)}>
            <option value="">— vyberte kód zprávy —</option>
            {codes
              .filter((c) => c.sender === 'hub')
              .map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
          </select>
          <BrandSelect value={seller} onChange={setSeller} allLabel={null} shopOnly />
          <select
            value={language}
            aria-label="Jazyk šablony"
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGES.map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
          {code === 'BC' ? (
            <input
              aria-label="Označení rozesílky"
              placeholder="označení, např. jarni-akce"
              value={variant}
              maxLength={60}
              onChange={(e) => setVariant(e.target.value.replace(/[^A-Za-z0-9-]/g, ''))}
            />
          ) : null}
          {detail.data?.template ? (
            <>
              <Pill tone={detail.data.template.publishedVersion ? 'ok' : 'warn'}>
                {detail.data.template.publishedVersion
                  ? `publikovaná verze ${detail.data.template.publishedVersion}`
                  : 'nepublikováno'}
              </Pill>
              {detail.data.template.draftVersion ? (
                <Pill tone="warn">rozepsaný koncept</Pill>
              ) : null}
            </>
          ) : null}
          {selected ? (
            <Pill tone={selected.channel === 'marketing' ? 'warn' : 'ok'}>
              {labelOf(CHANNEL_LABEL, selected.channel)}
            </Pill>
          ) : null}
        </div>
      </Panel>

      {!code ? (
        <EmptyState title="Vyberte šablonu" icon="file">
          Zvolte kód zprávy, prodejce a jazyk, nebo otevřete šablonu ze seznamu výše.
        </EmptyState>
      ) : null}

      {/* Editor je okno přes celou šířku (D-315): seznam zůstává pod ním na místě, Esc a křížek ho
          zavřou a neuložené změny se hlídají. */}
      <Dialog
        open={code !== ''}
        full
        title={`${selected?.name ?? code} (${code} · ${seller} · ${language})`}
        testId="template-editor-dialog"
        onClose={() => setCode('')}
        beforeClose={() => !dirty || window.confirm('Máte neuložené změny. Opravdu zavřít editor?')}
      >
        {code ? (
          <>
            <div className="crumbs">
              <button className="linkbtn" onClick={() => setCode('')}>
                <Icon name="back" size={14} /> Šablony
              </button>
              <Icon name="chevron" size={12} />
              <b>{selected?.name ?? code}</b>
              <span className="muted">
                {code} · {seller} · {language}
              </span>
            </div>
            {canEdit ? (
              <div className="actionbar" data-testid="template-actions">
                <div className="actionbar-txt">
                  <b>Co se stane při publikaci</b>
                  <span>
                    Zkontroluje se obsah a proměnné; verze se zveřejní.{' '}
                    {detail.data?.channel === 'marketing' || selected?.channel === 'marketing'
                      ? 'Marketingová šablona se pak sama nahraje do SmartEmailingu.'
                      : 'Servisní zpráva se posílá jako HTML z hubu, do SmartEmailingu se nenahrává.'}
                  </span>
                </div>
                <div className="row">
                  <ImagePickButton
                    label="Galerie obrázků"
                    disabled={!canEdit}
                    onPick={addImageFromGallery}
                  />
                  <button className="btn" onClick={() => save.mutate()}>
                    Uložit koncept
                  </button>
                  {rights.can('run_send_tests') ? (
                    <button
                      className="btn"
                      data-testid="template-test-send"
                      onClick={() => setTestOpen((v) => !v)}
                    >
                      Poslat test
                    </button>
                  ) : null}
                  <button type="button" className="btn pri" onClick={() => setAskPublish(true)}>
                    Publikovat
                  </button>
                </div>
              </div>
            ) : null}
            {testOpen && rights.can('run_send_tests') ? (
              <TestSendPanel templateKey={key} onClose={() => setTestOpen(false)} />
            ) : null}
            {sync || retrySync.isPending ? (
              <div
                className={`syncres ${retrySync.isPending ? 'neu' : sync?.ok ? 'ok' : 'bad'}`}
                role="status"
                data-testid="sync-result"
              >
                <Icon name={retrySync.isPending ? 'refresh' : sync?.ok ? 'check' : 'alert'} />
                <span>
                  {retrySync.isPending
                    ? 'Nahrávám do SmartEmailingu…'
                    : sync?.ok
                      ? sync.skipped
                        ? 'SmartEmailing už má stejnou verzi — nic se nenahrávalo.'
                        : `Nahráno do SmartEmailingu${sync.emailId ? ` (e-mail ${sync.emailId})` : ''}.`
                      : `Nahrání do SmartEmailingu selhalo: ${sync?.error ?? 'neznámá chyba'}`}
                </span>
                {!retrySync.isPending && sync && !sync.ok ? (
                  <button className="btn" onClick={() => retrySync.mutate()}>
                    Zkusit znovu
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="composer">
              <Panel title="Skladba e-mailu" icon="layers">
                {issues.length > 0 ? (
                  <div className="confirm dngc" role="alert">
                    <b>Publikace neprošla:</b>
                    <ul>
                      {issues.map((i) => (
                        <li key={i.code + i.detail}>{i.detail}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="help">
                  <p>
                    Skládejte e-mail z bloků: <b>přidejte</b> blok z nabídky, <b>přetáhněte</b> ho
                    na místo, nebo <b>klikněte na část e-mailu v náhledu</b> a upraví se její
                    nastavení. Změny se hned promítnou doprava.
                  </p>
                </div>

                {canEdit ? (
                  <label className="field">
                    <span>Začít ze vzoru</span>
                    <select
                      value=""
                      onChange={(e) => {
                        const preset = PRESETS.find(
                          (p) => starterTemplateKey(p) === e.target.value,
                        );
                        if (!preset) return;
                        setDoc(JSON.parse(JSON.stringify(preset.doc)) as Doc);
                        setCode(preset.code);
                        setSeller(preset.seller);
                        setLanguage(preset.language);
                        setOpenIndex(null);
                      }}
                    >
                      <option value="">— prázdná šablona —</option>
                      {PRESETS.map((preset) => (
                        <option key={starterTemplateKey(preset)} value={starterTemplateKey(preset)}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <label className="field">
                  <span>Předmět</span>
                  <input
                    value={doc.subject}
                    disabled={!canEdit}
                    onChange={(e) => setDoc({ ...doc, subject: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Preheader — text za předmětem ve výpisu schránky</span>
                  <input
                    value={doc.preheader ?? ''}
                    disabled={!canEdit}
                    onChange={(e) => setDoc({ ...doc, preheader: e.target.value })}
                  />
                </label>

                {canEdit ? (
                  <>
                    <h4 className="mini">
                      Přidat blok{' '}
                      <span className="note">
                        {openIndex === null ? '— vloží se na konec' : '— vloží se pod vybraný'}
                      </span>
                    </h4>
                    <div className="palette">
                      {(Object.keys(BLOCK_LABELS) as BlockType[])
                        .filter((type) => type !== 'orderitems' || code.startsWith('T'))
                        .map((type) => (
                          <button
                            key={type}
                            type="button"
                            className="pal s2b-drag"
                            title={`${BLOCK_HINT[type]} Kliknutím se přidá, nebo ho přetáhněte na místo mezi bloky.`}
                            onPointerDown={(e) =>
                              dnd.begin(e, { kind: 'new', type, label: BLOCK_LABELS[type] })
                            }
                            onClick={() => {
                              if (!dnd.consumeClick()) addBlock(type);
                            }}
                          >
                            <span className="ico">{BLOCK_ICON[type]}</span>
                            {BLOCK_LABELS[type]}
                          </button>
                        ))}
                    </div>
                  </>
                ) : null}

                <h4 className="mini">Bloky e-mailu: pořadí a úpravy</h4>
                <p className="note">
                  Kliknutím na blok v seznamu níže otevřete jeho nastavení (texty, odkazy, obrázek
                  tlačítkem Galerie, jazykové verze řeší překlady).
                </p>
                {canEdit && doc.blocks.length > 1 ? (
                  <p className="note">
                    Blok přesunete uchopením za úchyt ⋮⋮ (myší i prstem) nebo tlačítky ↑ a ↓.
                  </p>
                ) : null}
                {doc.blocks.length === 0 && dnd.source === null ? (
                  <p className="note">Zatím žádný blok. Přidejte první z nabídky výše.</p>
                ) : null}
                <p className="s2b-sr" role="status" aria-live="polite">
                  {announce}
                </p>
                <ol
                  ref={listRef}
                  className={`blocks${dnd.source ? ' dropping' : ''}${dnd.source && dnd.dropAt === null ? ' away' : ''}`}
                  data-testid="block-list"
                >
                  {doc.blocks.map((block, index) => {
                    const open = openIndex === index;
                    const dragged = dnd.source?.kind === 'move' && dnd.source.index === index;
                    return (
                      <Fragment key={index}>
                        {dnd.dropAt === index ? <DropMark label={dnd.source?.label ?? ''} /> : null}
                        <li className={`blk${open ? ' open' : ''}${dragged ? ' dragged' : ''}`}>
                          <div
                            className="blk-head"
                            onPointerDown={(e) => {
                              if ((e.target as HTMLElement).closest('.acts, .grip')) return;
                              dnd.begin(e, {
                                kind: 'move',
                                index,
                                label: BLOCK_LABELS[block.type],
                              });
                            }}
                            onClick={() => {
                              if (!dnd.consumeClick()) setOpenIndex(open ? null : index);
                            }}
                          >
                            {canEdit ? (
                              <span
                                className="grip"
                                title="Uchopte a přetáhněte na jiné místo"
                                aria-hidden="true"
                                onPointerDown={(e) =>
                                  dnd.begin(
                                    e,
                                    { kind: 'move', index, label: BLOCK_LABELS[block.type] },
                                    { immediate: true },
                                  )
                                }
                              >
                                ⋮⋮
                              </span>
                            ) : null}
                            <span className="ico">{BLOCK_ICON[block.type]}</span>
                            <b>{BLOCK_LABELS[block.type]}</b>
                            <span className="sum">{summary(block)}</span>
                            {canEdit ? (
                              <span className="acts" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="btn mv-up"
                                  disabled={index === 0}
                                  onClick={() => move(index, -1)}
                                  title="Posunout nahoru"
                                  aria-label={`Posunout nahoru: ${BLOCK_LABELS[block.type]}`}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="btn mv-down"
                                  disabled={index === doc.blocks.length - 1}
                                  onClick={() => move(index, 1)}
                                  title="Posunout dolů"
                                  aria-label={`Posunout dolů: ${BLOCK_LABELS[block.type]}`}
                                >
                                  ↓
                                </button>
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() => duplicateBlock(index)}
                                  title="Zdvojit"
                                  aria-label={`Zdvojit: ${BLOCK_LABELS[block.type]}`}
                                >
                                  ⧉
                                </button>
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() => removeBlock(index)}
                                  title="Smazat"
                                  aria-label={`Smazat: ${BLOCK_LABELS[block.type]}`}
                                >
                                  ×
                                </button>
                              </span>
                            ) : null}
                          </div>
                          {open ? (
                            <div className="blk-body">
                              <p className="note">{BLOCK_HINT[block.type]}</p>
                              <BlockFields
                                block={block}
                                disabled={!canEdit}
                                onChange={(patch) => update(index, patch)}
                              />
                            </div>
                          ) : null}
                        </li>
                      </Fragment>
                    );
                  })}
                  {dnd.dropAt === doc.blocks.length ? (
                    <DropMark label={dnd.source?.label ?? ''} />
                  ) : null}
                  {doc.blocks.length === 0 && dnd.source ? (
                    <li className="dropempty" aria-hidden="true">
                      Pusťte blok sem
                    </li>
                  ) : null}
                </ol>
                {dnd.source ? (
                  <div ref={dnd.ghostRef} className="s2b-ghost" aria-hidden="true">
                    {dnd.source.label}
                  </div>
                ) : null}

                {variables.length > 0 ? (
                  <p className="note">
                    Proměnné pro {code}, které jde použít v textech:{' '}
                    {variables.map((v) => `{{${v}}}`).join(', ')}
                  </p>
                ) : null}
              </Panel>

              <div className="sticky">
                <Panel
                  title="Náhled"
                  action={
                    <div className="row">
                      <span className="seg">
                        <button
                          className={`btn${scheme === 'light' ? ' pri' : ''}`}
                          onClick={() => setScheme('light')}
                          title="Jak e-mail uvidí příjemce se světlým nastavením"
                        >
                          ☀ Světlý
                        </button>
                        <button
                          className={`btn${scheme === 'dark' ? ' pri' : ''}`}
                          onClick={() => setScheme('dark')}
                          title="Jak e-mail uvidí příjemce s tmavým režimem (Apple Mail, iOS…)"
                        >
                          ☾ Tmavý
                        </button>
                      </span>
                      <span className="seg">
                        <button
                          className={`btn${device === 'desktop' ? ' pri' : ''}`}
                          onClick={() => setDevice('desktop')}
                        >
                          Počítač
                        </button>
                        <button
                          className={`btn${device === 'mobile' ? ' pri' : ''}`}
                          onClick={() => setDevice('mobile')}
                        >
                          Telefon
                        </button>
                      </span>
                    </div>
                  }
                >
                  <label className="field">
                    <span>Náhled s daty zákazníka (id)</span>
                    <input
                      value={customerId}
                      placeholder="např. C-0001"
                      onChange={(e) => setCustomerId(e.target.value)}
                    />
                  </label>
                  {preview.data?.missingVars?.length ? (
                    <p className="note" role="status">
                      Nedosazené proměnné: {preview.data.missingVars.join(', ')} — v e-mailu
                      zůstanou prázdné.
                    </p>
                  ) : null}
                  <iframe
                    ref={frameRef}
                    title="Náhled šablony"
                    sandbox="allow-same-origin"
                    className={`preview${device === 'mobile' ? ' s2b-phone' : ''}`}
                    onLoad={wireFrame}
                    srcDoc={preview.data?.html ?? ''}
                  />
                  <p className="note">
                    Klikněte na část e-mailu a otevře se její nastavení vlevo. Tmavý režim funguje
                    jen v klientech, které ho podporují — ostatní ukážou světlý.
                  </p>
                </Panel>
              </div>
            </div>

            {detail.data && detail.data.versions.length > 0 ? (
              <Panel title="Verze">
                <div className="scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Verze</th>
                        <th>Stav</th>
                        <th>Předmět</th>
                        <th>Publikováno</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {detail.data.versions.map((v) => (
                        <tr key={v.version}>
                          <td>{v.version}</td>
                          <td>{labelOf(VERSION_STATUS, v.status)}</td>
                          <td>{v.subject}</td>
                          <td>{v.publishedAt ? dateTime(v.publishedAt) : '—'}</td>
                          <td>
                            {canEdit && v.status === 'archived' ? (
                              <button
                                type="button"
                                className="btn"
                                aria-label={`Vrátit verzi ${v.version}`}
                                onClick={() => setAskRevert(v.version)}
                              >
                                Vrátit tuhle
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            ) : null}
          </>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={askPublish}
        title="Publikovat šablonu?"
        confirmLabel="Publikovat"
        busy={publish.isPending}
        onCancel={() => setAskPublish(false)}
        onConfirm={() => {
          setAskPublish(false);
          publish.mutate();
        }}
      >
        <p>
          Zkontroluje se obsah a proměnné a tato verze se zveřejní. Od té chvíle podle ní hub skládá
          zprávy.{' '}
          {selected?.channel === 'marketing'
            ? 'Marketingová šablona se pak sama nahraje do SmartEmailingu.'
            : 'Servisní zpráva se posílá jako HTML z hubu, do SmartEmailingu se nenahrává.'}
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={askRevert !== null}
        title={`Vrátit verzi ${askRevert ?? ''}?`}
        confirmLabel="Vrátit tuhle verzi"
        busy={revert.isPending}
        onCancel={() => setAskRevert(null)}
        onConfirm={() => {
          if (askRevert !== null) revert.mutate(askRevert);
          setAskRevert(null);
        }}
      >
        <p>Tato dřívější verze se znovu publikuje a hub podle ní hned začne skládat zprávy.</p>
      </ConfirmDialog>
    </>
  );
}

/**
 * Popis polí nových bloků. Jeden obecný vykreslovač místo ručně psaného
 * formuláře pro každý typ — přidání bloku je pak jen další záznam tady.
 */
interface Field {
  name: string;
  label: string;
  kind?: 'text' | 'area' | 'select' | 'number';
  options?: Array<[string, string]>;
}
interface Spec {
  fields: Field[];
  list?: { name: string; label: string; fields: Field[]; blank: Record<string, unknown> };
}

const BUTTON_FIELDS: Field[] = [
  { name: 'button.text', label: 'Text tlačítka' },
  { name: 'button.url', label: 'Odkaz tlačítka' },
];

const SPECS: Partial<Record<BlockType, Spec>> = {
  nav: {
    fields: [
      { name: 'brand', label: 'Značka' },
      { name: 'brandAccent', label: 'Značka — část v barvě (např. GP)' },
    ],
    list: {
      name: 'links',
      label: 'Odkaz',
      fields: [
        { name: 'text', label: 'Text' },
        { name: 'url', label: 'Odkaz' },
      ],
      blank: { text: 'Odkaz', url: 'https://' },
    },
  },
  cover: {
    fields: [
      { name: 'image', label: 'Adresa fotky' },
      { name: 'alt', label: 'Popis fotky' },
      { name: 'badge', label: 'Štítek (např. Prodej zahájen)' },
      { name: 'title', label: 'Nadpis' },
      { name: 'subtitle', label: 'Podnadpis' },
      ...BUTTON_FIELDS,
      { name: 'height', label: 'Výška (body)', kind: 'number' },
      {
        name: 'size',
        label: 'Velikost nadpisu',
        kind: 'select',
        options: [
          ['large', 'velký (úvod)'],
          ['medium', 'střední (pruh uprostřed)'],
        ],
      },
    ],
  },
  banner: {
    fields: [
      { name: 'image', label: 'Adresa fotky (volitelná)' },
      { name: 'alt', label: 'Popis fotky' },
      { name: 'eyebrow', label: 'Malý nadpis nad hlavním' },
      { name: 'badge', label: 'Štítek v oblém rámečku (např. F1 Monza 2027)' },
      { name: 'title', label: 'Nadpis' },
      { name: 'kicker', label: 'Červený řádek pod nadpisem' },
      { name: 'subtitle', label: 'Podnadpis' },
      ...BUTTON_FIELDS,
      { name: 'code.label', label: 'Box s kódem: popisek' },
      { name: 'code.value', label: 'Box s kódem: kód' },
      { name: 'note', label: 'Drobný řádek pod tlačítkem' },
      {
        name: 'tone',
        label: 'Barva pruhu',
        kind: 'select',
        options: [
          ['dark', 'tmavý'],
          ['accent', 'v barvě značky'],
        ],
      },
    ],
  },
  points: {
    fields: [],
    list: {
      name: 'items',
      label: 'Řádek',
      fields: [
        { name: 'icon', label: 'Ikona (emoji)' },
        { name: 'title', label: 'Titulek' },
        { name: 'text', label: 'Popis', kind: 'area' },
      ],
      blank: { icon: '', title: 'Titulek', text: '' },
    },
  },
  kicker: { fields: [{ name: 'text', label: 'Text' }] },
  deadline: {
    fields: [
      { name: 'title', label: 'Nadpis (*červená část*)' },
      { name: 'text', label: 'Text' },
      ...BUTTON_FIELDS,
      { name: 'note', label: 'Datum pod tlačítkem' },
    ],
  },
  signoff: {
    fields: [
      { name: 'text', label: 'Pozdrav' },
      { name: 'team', label: 'Podpis (červeně)' },
    ],
  },
  promo: {
    fields: [
      { name: 'label', label: 'Popisek (např. KÓD)' },
      { name: 'code', label: 'Kód' },
      { name: 'note', label: 'Podmínky (např. jen do neděle)' },
    ],
  },
  deal: {
    fields: [
      { name: 'badge', label: 'Štítek' },
      { name: 'title', label: 'Název' },
      { name: 'subtitle', label: 'Podtitul' },
      { name: 'desc', label: 'Popis', kind: 'area' },
      { name: 'image', label: 'Adresa obrázku (mapka, PNG nebo JPG)' },
      { name: 'alt', label: 'Popis obrázku' },
      { name: 'oldPrice', label: 'Přeškrtnutá cena' },
      { name: 'price', label: 'Cena' },
      { name: 'save', label: 'Odznak úspory (např. ušetříte 20 %)' },
      { name: 'fine', label: 'Drobný řádek pod cenou' },
      { name: 'strip', label: 'Pruh (např. kód; *červená část*)' },
      {
        name: 'stripTone',
        label: 'Barva pruhu',
        kind: 'select',
        options: [
          ['dark', 'černý'],
          ['pink', 'růžový'],
        ],
      },
      ...BUTTON_FIELDS,
    ],
  },
  ctarow: {
    fields: [
      { name: 'text', label: 'Text' },
      { name: 'url', label: 'Odkaz' },
      {
        name: 'tone',
        label: 'Vzhled',
        kind: 'select',
        options: [
          ['line', 'mezi dvěma čarami'],
          ['link', 'podtržený červený odkaz'],
        ],
      },
    ],
  },
  crosspromo: {
    fields: [
      { name: 'title', label: 'Nadpis' },
      { name: 'text', label: 'Text', kind: 'area' },
      { name: 'logo', label: 'Adresa loga (řádkové rozložení)' },
      { name: 'logoAlt', label: 'Popis loga' },
      { name: 'image', label: 'Adresa obrázku (mockupy)' },
      { name: 'alt', label: 'Popis obrázku' },
      ...BUTTON_FIELDS,
    ],
  },
  stats: {
    fields: [
      {
        name: 'tone',
        label: 'Vzhled',
        kind: 'select',
        options: [
          ['light', 'světlé dlaždice'],
          ['dark', 'černá sekce s červenými čísly'],
        ],
      },
      { name: 'title', label: 'Nadpis (jen černá sekce)' },
      { name: 'closing', label: 'Závěrečná věta (jen černá sekce)', kind: 'area' },
    ],
    list: {
      name: 'items',
      label: 'Dlaždice',
      fields: [
        { name: 'value', label: 'Hodnota' },
        { name: 'label', label: 'Popisek' },
      ],
      blank: { value: '0', label: 'popisek' },
    },
  },
  split: {
    fields: [
      { name: 'image', label: 'Adresa obrázku (volitelná)' },
      { name: 'alt', label: 'Popis obrázku' },
      {
        name: 'imageSide',
        label: 'Obrázek je',
        kind: 'select',
        options: [
          ['right', 'vpravo'],
          ['left', 'vlevo'],
        ],
      },
      { name: 'title', label: 'Nadpis' },
      { name: 'html', label: 'Text', kind: 'area' },
      ...BUTTON_FIELDS,
    ],
  },
  countdown: {
    fields: [
      { name: 'label', label: 'Popisek (např. Do závodu zbývá)' },
      { name: 'value', label: 'Hodnota (např. méně než týden)' },
      { name: 'note', label: 'Poznámka' },
    ],
  },
  quote: {
    fields: [
      { name: 'text', label: 'Text', kind: 'area' },
      { name: 'author', label: 'Autor (volitelný)' },
    ],
  },
  pricebox: {
    fields: [
      { name: 'label', label: 'Popisek' },
      { name: 'price', label: 'Cena' },
      ...BUTTON_FIELDS,
    ],
  },
  features: {
    fields: [],
    list: {
      name: 'items',
      label: 'Výhoda',
      fields: [
        { name: 'icon', label: 'Ikona (znak nebo emoji)' },
        { name: 'label', label: 'Popisek' },
      ],
      blank: { icon: '⭐', label: 'Výhoda' },
    },
  },
  offers: {
    fields: [],
    list: {
      name: 'items',
      label: 'Řádek',
      fields: [
        { name: 'image', label: 'Fotka' },
        { name: 'alt', label: 'Popis fotky' },
        { name: 'title', label: 'Název' },
        { name: 'desc', label: 'Popis', kind: 'area' },
        { name: 'price', label: 'Cena' },
        { name: 'button', label: 'Text tlačítka' },
        { name: 'url', label: 'Odkaz' },
      ],
      blank: { image: '', alt: '', title: 'Název', desc: '', price: '', button: '', url: '' },
    },
  },
  racelist: {
    fields: [],
    list: {
      name: 'items',
      label: 'Závod',
      fields: [
        { name: 'image', label: 'Fotka' },
        { name: 'alt', label: 'Popis fotky' },
        { name: 'series', label: 'Série (F1, MotoGP)' },
        { name: 'name', label: 'Název' },
        { name: 'meta', label: 'Termín a místo' },
        { name: 'price', label: 'Cena' },
        { name: 'button', label: 'Text tlačítka' },
        { name: 'url', label: 'Odkaz' },
      ],
      blank: {
        image: '',
        alt: '',
        series: 'F1',
        name: 'Název závodu',
        meta: '',
        price: '',
        button: 'Vybrat vstupenky →',
        url: '',
      },
    },
  },
  callout: {
    fields: [
      { name: 'icon', label: 'Ikona (emoji)' },
      { name: 'title', label: 'Nadpis' },
      { name: 'text', label: 'Text' },
      { name: 'button.text', label: 'Tlačítko — text' },
      { name: 'button.url', label: 'Tlačítko — odkaz' },
      { name: 'link.text', label: 'Odkaz vpravo — text' },
      { name: 'link.url', label: 'Odkaz vpravo — adresa' },
    ],
  },
};

const getPath = (obj: Record<string, unknown>, path: string): unknown =>
  path
    .split('.')
    .reduce<unknown>((acc, key) => (acc as Record<string, unknown> | undefined)?.[key], obj);

/** Hodnotu na tečkové cestě (`button.text`) vrátí jako změnu nejvyššího klíče. */
function setPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) return { [head as string]: value };
  const inner = (obj[head as string] ?? {}) as Record<string, unknown>;
  const next = { ...inner, ...setPath(inner, rest.join('.'), value) };
  /* Tlačítko nebo odkaz, který nemá ani text, ani adresu, se z bloku vyhodí. */
  const empty = Object.values(next).every((v) => v === '' || v === undefined);
  return { [head as string]: empty ? undefined : next };
}

function FieldInput({
  field,
  value,
  disabled,
  onChange,
}: {
  field: Field;
  value: unknown;
  disabled: boolean;
  onChange: (value: string | number) => void;
}): JSX.Element {
  const text = value === undefined || value === null ? '' : String(value);
  return (
    <label className="field">
      <span>{field.label}</span>
      {field.kind === 'select' ? (
        <select value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          {(field.options ?? []).map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </select>
      ) : field.kind === 'area' ? (
        <textarea
          rows={2}
          value={text}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : /(^|\.)(image|logo)$/.test(field.name) ? (
        <span className="field-with-pick">
          <input value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
          <ImagePickButton disabled={disabled} onPick={onChange} />
        </span>
      ) : (
        <input
          type={field.kind === 'number' ? 'number' : 'text'}
          value={text}
          disabled={disabled}
          onChange={(e) =>
            onChange(field.kind === 'number' ? Number(e.target.value) : e.target.value)
          }
        />
      )}
    </label>
  );
}

function SpecFields({
  spec,
  block,
  disabled,
  onChange,
}: {
  spec: Spec;
  block: Block;
  disabled: boolean;
  onChange: (patch: Partial<Block>) => void;
}): JSX.Element {
  const list = spec.list;
  const rows = list ? ((block[list.name] as Array<Record<string, unknown>> | undefined) ?? []) : [];
  const setRows = (next: Array<Record<string, unknown>>): void =>
    onChange({ [list?.name ?? 'items']: next });

  return (
    <>
      {spec.fields.map((field) => (
        <FieldInput
          key={field.name}
          field={field}
          value={getPath(block, field.name)}
          disabled={disabled}
          onChange={(v) => onChange(setPath(block, field.name, v) as Partial<Block>)}
        />
      ))}
      {list ? (
        <>
          {rows.map((row, index) => (
            <div className="block" key={index}>
              <div className="row">
                <b>
                  {list.label} {index + 1}
                </b>
                {!disabled ? (
                  <button
                    className="btn"
                    onClick={() => setRows(rows.filter((_, i) => i !== index))}
                    title="Smazat"
                  >
                    ×
                  </button>
                ) : null}
              </div>
              {list.fields.map((field) => (
                <FieldInput
                  key={field.name}
                  field={field}
                  value={row[field.name]}
                  disabled={disabled}
                  onChange={(v) =>
                    setRows(rows.map((r, i) => (i === index ? { ...r, [field.name]: v } : r)))
                  }
                />
              ))}
            </div>
          ))}
          {!disabled ? (
            <button className="btn" onClick={() => setRows([...rows, { ...list.blank }])}>
              + přidat {list.label.toLowerCase()}
            </button>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function BlockFields({
  block,
  disabled,
  onChange,
}: {
  block: Block;
  disabled: boolean;
  onChange: (patch: Partial<Block>) => void;
}): JSX.Element | null {
  const text = (name: string, label: string, value: string): JSX.Element => (
    <label className="field">
      <span>{label}</span>
      {name === 'image' || name === 'logo' || (block.type === 'image' && name === 'url') ? (
        <span className="field-with-pick">
          <input
            value={value}
            disabled={disabled}
            onChange={(e) => onChange({ [name]: e.target.value })}
          />
          <ImagePickButton disabled={disabled} onPick={(url) => onChange({ [name]: url })} />
        </span>
      ) : (
        <input
          value={value}
          disabled={disabled}
          onChange={(e) => onChange({ [name]: e.target.value })}
        />
      )}
    </label>
  );

  const spec = SPECS[block.type];
  if (spec) return <SpecFields spec={spec} block={block} disabled={disabled} onChange={onChange} />;

  const toggle = (name: string, label: string, value: unknown): JSX.Element => (
    <label className="row">
      <input
        type="checkbox"
        checked={Boolean(value)}
        disabled={disabled}
        onChange={(e) => onChange({ [name]: e.target.checked || undefined })}
      />
      <span className="note">{label}</span>
    </label>
  );

  switch (block.type) {
    case 'hero': {
      const button = (block.button ?? {}) as { text?: string; url?: string };
      const setButton = (patch: Record<string, string>): void =>
        onChange({ button: { ...button, ...patch } });
      return (
        <>
          {text('image', 'Adresa fotky (celá šířka)', String(block.image ?? ''))}
          {text('alt', 'Popis fotky (povinný)', String(block.alt ?? ''))}
          {text('eyebrow', 'Malý nadpis nad hlavním', String(block.eyebrow ?? ''))}
          {text('title', 'Hlavní nadpis', String(block.title ?? ''))}
          <label className="field">
            <span>Úvodní text</span>
            <textarea
              rows={5}
              value={String(block.html ?? '')}
              disabled={disabled}
              onChange={(e) => onChange({ html: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Text tlačítka</span>
            <input
              value={button.text ?? ''}
              disabled={disabled}
              onChange={(e) => setButton({ text: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Odkaz tlačítka</span>
            <input
              value={button.url ?? ''}
              disabled={disabled}
              onChange={(e) => setButton({ url: e.target.value })}
            />
          </label>
        </>
      );
    }
    case 'heading':
      return (
        <>
          {text('eyebrow', 'Malý nadpis nad hlavním (volitelný)', String(block.eyebrow ?? ''))}
          {text('text', 'Text', String(block.text ?? ''))}
          <label className="field">
            <span>Zarovnání</span>
            <select
              value={String(block.align ?? 'left')}
              disabled={disabled}
              onChange={(e) => onChange({ align: e.target.value })}
            >
              <option value="left">vlevo</option>
              <option value="center">na střed</option>
            </select>
          </label>
          <label className="field">
            <span>Úroveň</span>
            <select
              value={String(block.level ?? 2)}
              disabled={disabled}
              onChange={(e) => onChange({ level: Number(e.target.value) })}
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </label>
        </>
      );
    case 'text':
      return (
        <>
          <label className="field">
            <span>Text (tučně &lt;b&gt;, odkaz &lt;a href&gt;)</span>
            <textarea
              rows={4}
              value={String(block.html ?? '')}
              disabled={disabled}
              onChange={(e) => onChange({ html: e.target.value })}
            />
          </label>
          <div className="row">
            {toggle('muted', 'tlumená barva', block.muted)}
            <label className="field">
              <span>Velikost</span>
              <select
                value={String(block.size ?? 'normal')}
                disabled={disabled}
                onChange={(e) => onChange({ size: e.target.value })}
              >
                <option value="normal">běžná</option>
                <option value="small">malá (poznámka)</option>
              </select>
            </label>
            <label className="field">
              <span>Zarovnání</span>
              <select
                value={String(block.align ?? 'left')}
                disabled={disabled}
                onChange={(e) => onChange({ align: e.target.value })}
              >
                <option value="left">vlevo</option>
                <option value="center">na střed</option>
              </select>
            </label>
          </div>
        </>
      );
    case 'button':
      return (
        <>
          {text('text', 'Text', String(block.text ?? ''))}
          {text('url', 'Odkaz', String(block.url ?? ''))}
          <div className="row">
            {toggle('fullWidth', 'přes celou šířku', block.fullWidth)}
            <label className="field">
              <span>Tvar</span>
              <select
                value={String(block.shape ?? 'pill')}
                disabled={disabled}
                onChange={(e) => onChange({ shape: e.target.value })}
              >
                <option value="pill">oblé</option>
                <option value="square">hranaté, velká písmena</option>
              </select>
            </label>
          </div>
        </>
      );
    case 'image':
      return (
        <>
          {text('url', 'Adresa obrázku', String(block.url ?? ''))}
          {text('alt', 'Popis (povinný)', String(block.alt ?? ''))}
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            disabled={disabled}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = async () => {
                const dataBase64 = String(reader.result).split(',')[1] ?? '';
                const up = await api.post<{ url: string; warning: string | null }>(
                  '/api/template-assets',
                  { filename: file.name, contentType: file.type, dataBase64 },
                );
                onChange({ url: up.url, ...(file.type === 'image/gif' ? { animated: true } : {}) });
                if (up.warning) window.alert(up.warning);
              };
              reader.readAsDataURL(file);
            }}
          />
        </>
      );
    case 'cards': {
      const items = (block.items ?? []) as Array<Record<string, string>>;
      const patch = (index: number, field: string, value: string): void =>
        onChange({
          items: items.map((it, i) => (i === index ? { ...it, [field]: value } : it)),
        });
      const cardField = (index: number, field: string, label: string): JSX.Element => (
        <label className="field" key={field}>
          <span>{label}</span>
          <input
            value={items[index]?.[field] ?? ''}
            disabled={disabled}
            onChange={(e) => patch(index, field, e.target.value)}
          />
        </label>
      );
      return (
        <>
          <label className="field">
            <span>Sloupcu</span>
            <select
              value={String(block.columns ?? 2)}
              disabled={disabled}
              onChange={(e) => onChange({ columns: Number(e.target.value) })}
            >
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </label>
          {items.map((item, index) => (
            <div className="block" key={index}>
              <div className="row">
                <b>Karta {index + 1}</b>
                {!disabled ? (
                  <button
                    className="btn"
                    onClick={() => onChange({ items: items.filter((_, i) => i !== index) })}
                  >
                    x
                  </button>
                ) : null}
              </div>
              {cardField(index, 'title', 'Nazev')}
              {cardField(index, 'subtitle', 'Termin nebo podtitulek')}
              {cardField(index, 'image', 'Obrazek')}
              {cardField(index, 'alt', 'Popis obrazku (povinny)')}
              {cardField(index, 'url', 'Odkaz')}
              {cardField(index, 'button', 'Text tlacitka')}
            </div>
          ))}
          {!disabled ? (
            <button
              className="btn"
              onClick={() =>
                onChange({
                  items: [
                    ...items,
                    { title: 'Novy zavod', alt: '', image: '', url: '', button: 'Koupit' },
                  ],
                })
              }
            >
              + pridat kartu
            </button>
          ) : null}
        </>
      );
    }
    case 'footer': {
      const links = ((block.links as Array<{ text: string; url: string }> | undefined) ?? []).map(
        (l) => ({ ...l }),
      );
      return (
        <>
          {text('note', 'Dovětek nad identifikací', String(block.note ?? ''))}
          {toggle('compact', 'kompaktní (bez kontaktů a sítí)', block.compact)}
          {links.map((item, index) => (
            <div className="row" key={index}>
              <input
                value={item.text}
                placeholder="Text odkazu"
                disabled={disabled}
                onChange={(e) =>
                  onChange({
                    links: links.map((l, i) => (i === index ? { ...l, text: e.target.value } : l)),
                  })
                }
              />
              <input
                value={item.url}
                placeholder="https://"
                disabled={disabled}
                onChange={(e) =>
                  onChange({
                    links: links.map((l, i) => (i === index ? { ...l, url: e.target.value } : l)),
                  })
                }
              />
              {!disabled ? (
                <button
                  className="btn"
                  onClick={() => onChange({ links: links.filter((_, i) => i !== index) })}
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}
          {!disabled ? (
            <button
              className="btn"
              onClick={() => onChange({ links: [...links, { text: 'Odkaz', url: 'https://' }] })}
            >
              + přidat odkaz do patičky
            </button>
          ) : null}
        </>
      );
    }
    case 'trips':
    case 'orderitems':
      return text('heading', 'Nadpis bloku', String(block.heading ?? ''));
    default:
      return null;
  }
}
